import { isDrivable, isIslandEligible, OFFERED_ISLANDS, requiresDrivable, US_STATES } from '../../shared/schema'
import { clearFieldError } from './validation'

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`#${id} missing`)
  return el as T
}

/** Show/hide a conditional wrapper; clear its inputs (+ errors) when hidden. */
function toggle(wrap: HTMLElement, show: boolean): void {
  wrap.classList.toggle('hidden', !show)
  if (!show) {
    for (const field of wrap.querySelectorAll<FormField>('input, select, textarea')) {
      field.value = ''
      clearFieldError(field)
    }
  }
}

export function initConditionalLogic(): void {
  const state = byId<HTMLSelectElement>('state')
  const islandWrap = byId<HTMLElement>('islandWrap')
  const island = byId<HTMLSelectElement>('island')
  const islandOtherWrap = byId<HTMLElement>('islandOtherWrap')
  const islandNotice = byId<HTMLElement>('islandNotice')
  const mauiIslandNotice = byId<HTMLElement>('mauiIslandNotice')
  const mauiDrivableNotice = byId<HTMLElement>('mauiDrivableNotice')
  const runningCondition = byId<HTMLSelectElement>('runningCondition')

  const hearAboutUs = byId<HTMLSelectElement>('hearAboutUs')
  const hearAboutUsOtherWrap = byId<HTMLElement>('hearAboutUsOtherWrap')

  const vehicleType = byId<HTMLSelectElement>('vehicleType')
  const vehicleTypeOtherWrap = byId<HTMLElement>('vehicleTypeOtherWrap')

  const titleStatus = byId<HTMLSelectElement>('titleStatus')
  const lienNotice = byId<HTMLElement>('lienNotice')
  const lostTitleNotice = byId<HTMLElement>('lostTitleNotice')

  const sameAsDonorAddress = byId<HTMLInputElement>('sameAsDonorAddress')
  const pickupAddressWrap = byId<HTMLElement>('pickupAddressWrap')

  const pickupDate = byId<HTMLInputElement>('preferredPickupDate')

  // Full state list (display name shown, two-letter code submitted) from the
  // shared schema. Locked to Hawaii while the program is Hawaii-only — drop
  // the `disabled` attribute in index.html when it expands.
  state.replaceChildren(
    ...US_STATES.map(({ code, name }) => {
      const option = document.createElement('option')
      option.value = code
      option.textContent = name
      return option
    }),
  )
  state.value = 'HI'

  // Island options from the shared schema too (2026-10-16: NKF narrowed the
  // dropdown to Oahu and Maui — OFFERED_ISLANDS is where that is decided).
  // The placeholder option stays in index.html; everything after it is ours.
  island.replaceChildren(
    ...Array.from(island.options).filter((o) => o.value === ''),
    ...OFFERED_ISLANDS.map((name) => {
      const option = document.createElement('option')
      option.textContent = name
      return option
    }),
  )

  // The island question only applies to Hawaii.
  const syncIsland = () => {
    const isHawaii = state.value === 'HI'
    islandWrap.classList.toggle('hidden', !isHawaii)
    if (!isHawaii) {
      island.value = ''
      clearFieldError(island)
      toggle(islandOtherWrap, false)
      islandNotice.classList.add('hidden')
      mauiIslandNotice.classList.add('hidden')
      mauiDrivableNotice.classList.add('hidden')
    }
  }
  state.addEventListener('change', syncIsland)
  syncIsland()

  // Maui accepts drivable vehicles only (NKF 2026-10-20). The island-level
  // notice warns as soon as Maui is picked; the step-2 notice explains the
  // block once a non-drivable condition is chosen. The schema's
  // crossFieldIssues does the actual blocking on both steps and the server.
  const syncDrivableNotice = () => {
    const show = requiresDrivable(island.value) && runningCondition.value !== '' && !isDrivable(runningCondition.value)
    mauiDrivableNotice.classList.toggle('hidden', !show)
  }

  island.addEventListener('change', () => {
    toggle(islandOtherWrap, island.value === 'Other island')
    islandNotice.classList.toggle('hidden', island.value === '' || isIslandEligible(island.value))
    mauiIslandNotice.classList.toggle('hidden', !requiresDrivable(island.value))
    syncDrivableNotice()
  })
  runningCondition.addEventListener('change', syncDrivableNotice)

  hearAboutUs.addEventListener('change', () => {
    toggle(hearAboutUsOtherWrap, hearAboutUs.value === 'Other')
  })

  vehicleType.addEventListener('change', () => {
    toggle(vehicleTypeOtherWrap, vehicleType.value === 'Other')
  })

  titleStatus.addEventListener('change', () => {
    lienNotice.classList.toggle('hidden', titleStatus.value !== 'Lien on title')
    lostTitleNotice.classList.toggle('hidden', titleStatus.value !== 'Lost title')
  })

  sameAsDonorAddress.addEventListener('change', () => {
    toggle(pickupAddressWrap, !sameAsDonorAddress.checked)
  })

  // Pickup dates in the past make no sense — floor the date picker at today.
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  pickupDate.min = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
